// src/components/SuggestionNudge.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { Lightbulb, X, ArrowRight } from "lucide-react";
import api from "../api/api";

const VISITOR_KEY = "sugg_nudge_last";
const SESSION_KEY = "sugg_nudge_session";
const LAST_TX_KEY = "sugg_nudge_last_tx";

const VISITOR_COOLDOWN_MS = 3 * 24 * 60 * 60 * 1000;
const VISITOR_DELAY_MS = 10000;
const LOGIN_DELAY_MS = 2500;
const PURCHASE_DELAY_MS = 3000;
const VISIBLE_MS = 8000;
const POLL_MS = 15000;

const QUALIFYING_TYPES = ["debit", "purchase", "rebuy"];
const HIDDEN_PREFIXES = ["/admin", "/login", "/signup", "/verify-email", "/check-email", "/reset-password"];

const MESSAGES = {
  visitor: { title: "Got an idea?", body: "Tell us what we can improve." },
  login: { title: "Welcome back!", body: "Got an idea to make LarryDigitals better?" },
  purchase: { title: "Thanks for your purchase!", body: "How was it? Tell us how we can do better." },
};

function getStore(kind) {
  try {
    return kind === "session" ? window.sessionStorage : window.localStorage;
  } catch (e) {
    return null;
  }
}

function readKey(kind, key) {
  try {
    const store = getStore(kind);
    return store ? store.getItem(key) : null;
  } catch (e) {
    return null;
  }
}

function writeKey(kind, key, value) {
  try {
    const store = getStore(kind);
    if (store) store.setItem(key, value);
  } catch (e) {
    // ignore storage errors
  }
}

function removeKey(kind, key) {
  try {
    const store = getStore(kind);
    if (store) store.removeItem(key);
  } catch (e) {
    // ignore storage errors
  }
}

const hasToken = () => !!readKey("local", "accessToken");

export default function SuggestionNudge() {
  const { pathname } = useLocation();
  const [nudge, setNudge] = useState(null);
  const showingRef = useRef(false);
  const hideTimerRef = useRef(null);
  const blocked = HIDDEN_PREFIXES.some((p) => pathname.startsWith(p));

  const hide = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    showingRef.current = false;
    setNudge(null);
  }, []);

  const display = useCallback(
    (mode) => {
      if (showingRef.current) return;
      showingRef.current = true;
      setNudge(mode);
      hideTimerRef.current = setTimeout(hide, VISIBLE_MS);
    },
    [hide]
  );

  useEffect(() => {
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  // Trigger 1: visitors (landing page, every 3 days) and logins (every login)
  useEffect(() => {
    const loggedIn = hasToken();
    if (!loggedIn) removeKey("session", SESSION_KEY);

    if (blocked) {
      hide();
      return undefined;
    }

    let delay;
    let mode;
    let markShown;

    if (loggedIn) {
      if (readKey("session", SESSION_KEY)) return undefined;
      delay = LOGIN_DELAY_MS;
      mode = "login";
      markShown = () => writeKey("session", SESSION_KEY, "1");
    } else {
      const last = Number(readKey("local", VISITOR_KEY)) || 0;
      if (Date.now() - last < VISITOR_COOLDOWN_MS) return undefined;
      delay = VISITOR_DELAY_MS;
      mode = "visitor";
      markShown = () => writeKey("local", VISITOR_KEY, String(Date.now()));
    }

    const timer = setTimeout(() => {
      markShown();
      display(mode);
    }, delay);

    return () => clearTimeout(timer);
  }, [pathname, blocked, display, hide]);

  // Trigger 2: after a new purchase (watches for a new purchase transaction)
  useEffect(() => {
    if (blocked || !hasToken()) return undefined;

    let cancelled = false;
    let failures = 0;
    let showTimer = null;

    const check = async () => {
      if (cancelled || failures >= 3 || document.hidden || !hasToken()) return;
      try {
        const res = await api.get("/transactions/me", { params: { limit: 5 } });
        failures = 0;
        const list = (res.data && res.data.transactions) || [];
        const latest = list.find((t) => QUALIFYING_TYPES.includes(t.type) && t.status !== "failed");
        const latestTime = latest ? new Date(latest.createdAt).getTime() : 0;
        const stored = Number(readKey("local", LAST_TX_KEY)) || 0;

        if (!stored) {
          writeKey("local", LAST_TX_KEY, String(latestTime || Date.now()));
          return;
        }

        if (latestTime > stored) {
          writeKey("local", LAST_TX_KEY, String(latestTime));
          if (showTimer) clearTimeout(showTimer);
          showTimer = setTimeout(() => {
            if (!cancelled) display("purchase");
          }, PURCHASE_DELAY_MS);
        }
      } catch (e) {
        failures += 1;
      }
    };

    check();
    const interval = setInterval(check, POLL_MS);
    const onVisible = () => {
      if (!document.hidden) check();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      clearInterval(interval);
      if (showTimer) clearTimeout(showTimer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [blocked, display]);

  const openSuggestion = () => {
    hide();
    const box = document.getElementById("suggestions");
    if (box) {
      box.scrollIntoView({ behavior: "smooth", block: "center" });
    } else {
      window.dispatchEvent(new Event("open-suggestion-form"));
    }
  };

  if (!nudge || blocked) return null;

  const msg = MESSAGES[nudge] || MESSAGES.visitor;

  return createPortal(
    <div
      className="fixed bottom-36 md:bottom-20 right-3 w-72 max-w-[calc(100vw-1.5rem)] animate-popIn"
      style={{ zIndex: 2147482000 }}
      role="status"
      aria-live="polite"
    >
      <style>{`@keyframes nudgeBar { from { width: 100%; } to { width: 0%; } }`}</style>
      <div className="relative overflow-hidden rounded-2xl bg-white dark:bg-gray-800 border border-purple-100 dark:border-purple-800/60 shadow-[0_12px_40px_-12px_rgba(91,33,182,0.45)]">
        <div className="flex items-start gap-3 p-3.5 pr-10">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-purple-100 dark:bg-purple-900/40">
            <Lightbulb size={19} className="text-purple-600 dark:text-purple-300" />
          </span>
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-gray-900 dark:text-white leading-tight">{msg.title}</p>
            <p className="mt-0.5 text-[11.5px] text-gray-500 dark:text-gray-400 leading-snug">{msg.body}</p>
            <button
              type="button"
              onClick={openSuggestion}
              className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-purple-600 hover:bg-purple-700 px-3.5 py-1.5 text-[11.5px] font-semibold text-white transition"
            >
              Send suggestion <ArrowRight size={12} />
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={hide}
          aria-label="Close"
          className="absolute top-2 right-2 p-1.5 rounded-full text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition"
        >
          <X size={14} />
        </button>

        <div className="h-1 w-full bg-purple-100 dark:bg-purple-900/40">
          <div
            key={nudge}
            className="h-full rounded-full bg-purple-500"
            style={{ animation: "nudgeBar " + VISIBLE_MS + "ms linear forwards" }}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}